import { useCallback, useState, type PointerEventHandler } from 'react';

import { resolveTooltipAt } from '../../../model/screencast-tooltips/screencastTooltipResolver';
import type { ResolvedTooltip, ScreencastTooltips } from '../../../model/screencast-tooltips/types';

import type { GetImagePointFromClientPosition, TooltipScreenPosition } from './types';

type UseScreencastHoverTooltipParams = {
  isEnabled: boolean;
  screencastTooltips?: ScreencastTooltips | null;
  getImagePointFromClientPosition: GetImagePointFromClientPosition;
};

export function useScreencastHoverTooltip({
  isEnabled,
  screencastTooltips,
  getImagePointFromClientPosition,
}: UseScreencastHoverTooltipParams) {
  const [resolvedTooltip, setResolvedTooltip] = useState<ResolvedTooltip | null>(null);
  const [tooltipScreenPosition, setTooltipScreenPosition] = useState<TooltipScreenPosition | null>(null);

  const clearTooltip = useCallback(() => {
    setResolvedTooltip(null);
    setTooltipScreenPosition(null);
  }, []);

  const handlePointerMove: PointerEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (!isEnabled || !screencastTooltips) {
        clearTooltip();
        return;
      }

      const imagePoint = getImagePointFromClientPosition(event.currentTarget, event.clientX, event.clientY);
      if (!imagePoint) {
        clearTooltip();
        return;
      }

      const nextResolvedTooltip = resolveTooltipAt(screencastTooltips, imagePoint.x, imagePoint.y);
      if (!nextResolvedTooltip) {
        clearTooltip();
        return;
      }

      setResolvedTooltip(nextResolvedTooltip);
      setTooltipScreenPosition({
        x: event.clientX,
        y: event.clientY,
      });
    },
    [clearTooltip, getImagePointFromClientPosition, isEnabled, screencastTooltips],
  );

  const handleMouseLeave = useCallback(() => {
    clearTooltip();
  }, [clearTooltip]);

  return {
    resolvedTooltip,
    tooltipScreenPosition,
    clearTooltip,
    handlePointerMove,
    handleMouseLeave,
  };
}
